import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { rateLimiter } from '../services/rateLimiter';

interface SearchBoxProps {
  onSearch: (query: string) => void;
  compact?: boolean;
  isLoading?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 600px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input<{ compact?: boolean }>`
  width: 100%;
  padding: ${props => props.compact ? '12px 50px 12px 16px' : '18px 60px 18px 24px'};
  font-size: ${props => props.compact ? '14px' : '18px'};
  font-family: var(--font-body);
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  border-radius: ${props => props.compact ? '8px' : '12px'};
  outline: none;
  transition: all 0.3s ease;
  box-shadow: ${props => props.compact ? 'none' : 'var(--shadow-sm)'};
  box-sizing: border-box;

  &:focus {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
    box-shadow: ${props => props.compact ? 
      '0 0 0 2px var(--color-primary-light, rgba(99, 102, 241, 0.1))' : 
      'var(--shadow-lg), 0 0 0 2px var(--color-primary-light, rgba(99, 102, 241, 0.1))'
    };
    transform: ${props => props.compact ? 'none' : 'translateY(-1px)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--color-surface);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const SubmitButton = styled.button<{ compact?: boolean; visible?: boolean }>`
  position: absolute;
  right: ${props => props.compact ? '8px' : '12px'};
  top: 50%;
  transform: translateY(-50%);
  width: ${props => props.compact ? '32px' : '36px'};
  height: ${props => props.compact ? '32px' : '36px'};
  background: var(--color-primary);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.compact ? '16px' : '18px'};
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-primary-hover, var(--color-primary));
    transform: translateY(-50%) scale(1.05);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: var(--color-text-secondary);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SearchBox: React.FC<SearchBoxProps> = ({ onSearch, compact = false, isLoading = false }) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [rateLimitCooldown, setRateLimitCooldown] = useState(0);

  // Check rate limit status periodically
  useEffect(() => {
    const checkRateLimit = () => {
      const cooldown = rateLimiter.getRemainingCooldown('search');
      setRateLimitCooldown(cooldown);
    };

    checkRateLimit();
    const interval = setInterval(checkRateLimit, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim() && !isLoading && rateLimitCooldown === 0) {
      onSearch(query.trim());
    }
  };

  const showButton = query.trim().length > 0 || isFocused;

  return (
    <Container>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <InputWrapper>
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={compact ? "Search..." : "What fandom mystery are you exploring?"}
            autoFocus={!compact}
            compact={compact}
            disabled={isLoading}
          />
          <SubmitButton
            type="submit"
            compact={compact}
            visible={showButton}
            disabled={isLoading || !query.trim() || rateLimitCooldown > 0}
            title={
              isLoading ? 'Searching...' : 
              rateLimitCooldown > 0 ? `Wait ${rateLimitCooldown}s` : 
              'Search'
            }
          >
            {isLoading ? <LoadingSpinner /> : rateLimitCooldown > 0 ? rateLimitCooldown : '→'}
          </SubmitButton>
        </InputWrapper>
      </form>
    </Container>
  );
};

export default SearchBox;